/* Attendance + payroll helpers — extracted verbatim from the original
   AdminPanel.jsx monolith so behaviour is identical.

   Policy (Indian SME real-estate firm):
     - Per-day pay = base monthly salary ÷ working days in month (weekend excluded)
     - Present:    full day's pay
     - Half day:   50% pay (auto-fires when clock-in > start + grace)
     - Absent:     no pay (no penalty)
     - Leave:      no pay, no penalty
     - Paid Leave: full pay, no penalty                              */

export const BASE_SALARY = 20000;
export const UNPLANNED_ABSENCE_PENALTY = 500;

export const DEFAULT_OFFICE_TIMING = {
  startHHMM: "09:30",
  endHHMM: "18:30",
  graceMinutes: 15,
  weekendDay: 0, // 0 = Sunday
};

export const ATTENDANCE_STATUS_META = {
  present:    { label: "Present",    cls: "bg-green-50 text-green-700 ring-green-600/20",     dot: "bg-green-500",  salaryWeight: 1   },
  half_day:   { label: "Half Day",   cls: "bg-amber-50 text-amber-700 ring-amber-600/20",     dot: "bg-amber-500",  salaryWeight: 0.5 },
  absent:     { label: "Absent",     cls: "bg-red-50 text-red-700 ring-red-600/20",           dot: "bg-red-500",    salaryWeight: 0   },
  leave:      { label: "Leave",      cls: "bg-blue-50 text-blue-700 ring-blue-600/20",        dot: "bg-blue-500",   salaryWeight: 0   },
  paid_leave: { label: "Paid Leave", cls: "bg-violet-50 text-violet-700 ring-violet-600/20",  dot: "bg-violet-500", salaryWeight: 1   },
};

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/* The app went live in September 2026. Nothing before that exists in the
   system, so offering those months listed 24 fabricated periods -- and
   yearlyPnl invented payroll for every one of them via the
   `allSalesPeople.length * BASE_SALARY` fallback.

   Also the cutoff for auto-absent (see summariseAttendance). */
export const APP_LAUNCH_YEAR = 2026;
export const APP_LAUNCH_MONTH = 8; // September, 0-indexed

/* Launch month through current + 6, so advance leave can still be planned
   ahead without a 36-entry list. Grows naturally as time passes. */
export const BILLING_MONTHS = (() => {
  const now = new Date();
  const labels = [];
  const start = new Date(APP_LAUNCH_YEAR, APP_LAUNCH_MONTH, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 6, 1);
  for (let d = new Date(start); d <= end; d.setMonth(d.getMonth() + 1)) {
    labels.push(`${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`);
  }
  return Array.from(new Set(labels));
})();

export function timeStringToMinutes(s) {
  if (!s) return 0;
  const [hh, mm] = s.split(":").map(Number);
  return (hh || 0) * 60 + (mm || 0);
}

export function deriveStatusFromClockIn(at, timing = DEFAULT_OFFICE_TIMING) {
  const startMin = timeStringToMinutes(timing.startHHMM);
  const grace = timing.graceMinutes ?? 15;
  // Office hours are IST (UTC+5:30); compare in IST minutes-of-day.
  const IST_OFFSET_MIN = 330;
  const inMin = (at.getUTCHours() * 60 + at.getUTCMinutes() + IST_OFFSET_MIN) % (24 * 60);
  return inMin > startMin + grace ? "half_day" : "present";
}

export function formatDurationMs(ms) {
  if (!ms || ms < 0) return "—";
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m}m`;
}

export function parseBillingMonth(label) {
  const [mname, yr] = label.split(" ");
  return { year: parseInt(yr, 10), month: MONTH_NAMES.indexOf(mname) };
}

export function daysInMonth(label) {
  const { year, month } = parseBillingMonth(label);
  return new Date(year, month + 1, 0).getDate();
}

export function workingDaysIn(label, weekendDay = 0) {
  const { year, month } = parseBillingMonth(label);
  const total = daysInMonth(label);
  let working = 0;
  for (let d = 1; d <= total; d++) {
    if (new Date(year, month, d).getDay() !== weekendDay) working++;
  }
  return working;
}

export function dateKeyFor(label, day) {
  const { year, month } = parseBillingMonth(label);
  const mm = String(month + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

/* True when `dateKey` ("YYYY-MM-DD") is a working day that has fully
   elapsed with nothing recorded -- i.e. one that should have been marked
   and wasn't. The single definition of auto-absent; summariseAttendance
   counts it for payroll, and the roster / calendar / my-attendance screens
   call this so a day reads the same everywhere.

   Today only qualifies once the office closing time has passed. Future
   days never do. Nothing before the launch month does either -- the
   system did not exist, so those gaps are not absences. */
export function isAutoAbsent(dateKey, weekendDay = 0, endHHMM = null, joiningDate = null) {
  if (!dateKey || !endHHMM) return false;
  const [y, mo, d] = String(dateKey).split("-").map(Number);
  if (!y || !mo || !d) return false;
  const month = mo - 1;
  if (y < APP_LAUNCH_YEAR || (y === APP_LAUNCH_YEAR && month < APP_LAUNCH_MONTH)) return false;
  /* A day before someone joined is not an absence -- they were not employed.
     RISHA joined on 30 September and showed 25 unplanned absences for days
     the company had no relationship with her.

     Same shape as the launch-month guard above: both say "this day was never
     this person's to attend". Everyone else joined in 2023-24, before the
     launch month, so for them this comparison is always already satisfied
     and their figures cannot move. "YYYY-MM-DD" compares correctly as a
     string, which is why dateKey is used directly. */
  if (joiningDate && String(dateKey) < String(joiningDate)) return false;
  if (new Date(y, month, d).getDay() === weekendDay) return false;
  const [eh, em] = String(endHHMM).split(":").map(Number);
  return new Date() > new Date(y, month, d, eh || 0, em || 0);
}

/* Returns `log` plus a synthetic row for every working day in `monthLabel`
   that has elapsed with nothing recorded, so the history tables read the
   same as the calendar and payroll. Synthetic rows carry only date, status
   and an autoAbsent flag -- every other column already renders a dash when
   its field is missing, and the flag lets "Marked by" avoid claiming an
   admin marked a day nobody touched. */
export function withAutoAbsent(log, weekendDay = 0, endHHMM = null, monthLabel = null, joiningDate = null) {
  if (!endHHMM || !monthLabel) return log;
  const have = new Set(log.map((r) => r.date));
  const total = daysInMonth(monthLabel);
  const extra = [];
  for (let d = 1; d <= total; d++) {
    const key = dateKeyFor(monthLabel, d);
    if (have.has(key)) continue;
    if (!isAutoAbsent(key, weekendDay, endHHMM, joiningDate)) continue;
    extra.push({ date: key, status: "absent", autoAbsent: true });
  }
  return extra.length ? [...log, ...extra] : log;
}

/* Compute monthly summary from a daily log for one employee. */
/* `endHHMM` enables auto-absent: a working day that has fully elapsed with
   no attendance record counts as an unplanned absence. Today only counts
   once the office closing time has passed; future days never do.

   Only applies from the launch month onward -- earlier months have gaps
   because the system did not exist, and back-dating absences there would
   retroactively cut salaries for time nobody was tracked.

   Derived at read time, not stored: an admin marking the day later simply
   overrides it, and it self-corrects if the weekend or office hours change. */
export function summariseAttendance(log, monthLabel, weekendDay = 0, endHHMM = null, joiningDate = null) {
  const totalWorking = workingDaysIn(monthLabel, weekendDay);
  let present = 0, half_day = 0, absent = 0, leave = 0, paid_leave = 0;
  log.forEach((r) => {
    if (r.status === "present") present++;
    else if (r.status === "half_day") half_day++;
    else if (r.status === "absent") absent++;
    else if (r.status === "leave") leave++;
    else if (r.status === "paid_leave") paid_leave++;
  });
  const marked = present + half_day + absent + leave + paid_leave;

  let autoAbsent = 0;
  if (endHHMM) {
    const { year, month } = parseBillingMonth(monthLabel);
    const isBeforeLaunch =
      year < APP_LAUNCH_YEAR || (year === APP_LAUNCH_YEAR && month < APP_LAUNCH_MONTH);
    if (!isBeforeLaunch) {
      const now = new Date();
      const [eh, em] = String(endHHMM).split(":").map(Number);
      const total = daysInMonth(monthLabel);
      let elapsed = 0;
      const pad = (n) => String(n).padStart(2, "0");
      for (let d = 1; d <= total; d++) {
        const day = new Date(year, month, d);
        if (day.getDay() === weekendDay) continue;
        // Not employed yet -- see the note in isAutoAbsent.
        if (joiningDate && `${year}-${pad(month + 1)}-${pad(d)}` < String(joiningDate)) continue;
        const close = new Date(year, month, d, eh || 0, em || 0);
        if (now > close) elapsed++;
      }
      autoAbsent = Math.max(0, elapsed - marked);
    }
  }

  return {
    totalWorking, present, half_day,
    absent: absent + autoAbsent,
    leave, paid_leave, marked, autoAbsent,
  };
}

/* Apply professional payroll rules. Returns a breakdown so admin can see
   exactly where each rupee went. */
export function computePayroll(summary, baseSalary = BASE_SALARY) {
  const perDay = baseSalary / summary.totalWorking;
  const halfDays = summary.half_day;
  const fullAbsentDays = summary.absent + summary.leave;
  // Penalty removed by client request. An unplanned absence now costs the
  // day's pay only. Kept as a 0 rather than deleted because
  // BillingPayroll renders it conditionally (`absentPenalty > 0`), so a
  // zero simply hides the row -- and any stored historical figure still
  // has a field to land in.
  const absentPenalty = 0;
  const halfDayDeduction = Math.round(perDay * halfDays * 0.5);
  const absentDeduction = Math.round(perDay * fullAbsentDays);
  const earned = Math.max(0, baseSalary - halfDayDeduction - absentDeduction);
  const totalDeduction = halfDayDeduction + absentDeduction + absentPenalty;
  const net = Math.max(0, earned - absentPenalty);
  return {
    baseSalary, perDay: Math.round(perDay),
    halfDays, halfDayDeduction,
    absentDays: fullAbsentDays, absentDeduction, absentPenalty,
    paidLeaveDays: summary.paid_leave,
    totalDeduction, net, earned,
  };
}

/* INR helper — Indian numbering with the ₹ glyph. */
export const inr = (n) => "₹" + (parseFloat(n) || 0).toLocaleString("en-IN");

/* Pipeline ordering for the Performance screen. "Visited" = lead has
   reached site_visit or beyond. */
export const PIPELINE_ORDER = ["new", "contacted", "qualified", "site_visit", "negotiation", "token", "booked"];
export const reachedVisit = (status) => {
  const idx = PIPELINE_ORDER.indexOf(status);
  return idx >= PIPELINE_ORDER.indexOf("site_visit");
};
